import { FilterList, SelectFilter, useQsFilters } from "@dash/filters";
import { Button } from "@dash/form";
import { Page, PageHeader } from "@dash/page";
import { SearchWithPagination } from "@dash/search";
import { QuerySkeleton } from "@dash/skeleton";
import fileDownload from "js-file-download";

import { useClientManager } from "@megaron/react-clients";
import { Serializers, SerializerValue } from "@megaron/serializers";

import { ProductAvailabilityTable } from "./ProductAvailabilityTable";

const filtersSerializer = Serializers.object({
  page: Serializers.integer,
  regions: Serializers.jsonString(Serializers.string.array()).optional(),
  searchTerm: Serializers.string.optional(),
});

export type ProductAvailabilityFilters = SerializerValue<typeof filtersSerializer>;

export const ProductAvailabilityHome = () => {
  const limit = 50;
  const regionsQuery = useClientManager("crm").regionsQuery().useQuery({});

  const { filters, setFilter } = useQsFilters(filtersSerializer, {
    page: 0,
    regions: undefined,
    searchTerm: "",
  });

  const productAvailabilityQuery = useClientManager("crm").productsAvailabilityReport().useQuery({
    offset: filters.page,
    limit,
    regions: filters.regions,
    searchTerm: filters.searchTerm,
  });

  const allItemsQuery = useClientManager("crm").productsAvailabilityReport().useQuery({
    offset: 0,
    limit: 11000,
    regions: filters.regions,
    searchTerm: filters.searchTerm,
  });

  const handleExport = () => {
    const rows = allItemsQuery.data?.items ?? [];

    const allProductNames = Array.from(new Set(rows.flatMap((row) => row.products.map((p) => p.itemName)))).sort();

    const header = ["Lp.", "POS", "Region", ...allProductNames];
    const csvRows = [header];

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const productMap = Object.fromEntries(
        row.products.map((p) => [
          p.itemName,
          `${translateStatus(p.status)}${p.date ? ` (${new Date(p.date).toLocaleDateString("pl-PL")})` : ""}`,
        ]),
      );

      const rowData = [
        (i + 1).toString(),
        row.posName,
        row.regionName ?? "",
        ...allProductNames.map((name) => productMap[name] ?? ""),
      ];
      csvRows.push(rowData);
    }

    const csvString = csvRows.map((r) => r.map(escapeCsvValue).join(",")).join("\n");
    fileDownload(csvString, "product-availability.csv");
  };

  const regionsOptions = regionsQuery.data
    ? regionsQuery.data.items.map((region) => ({
        label: region.name,
        value: region.name,
      }))
    : [];

  return (
    <Page
      css={{
        maxWidth: "1600px",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
      }}
    >
      <PageHeader actions={<Button onClick={handleExport}>Eksportuj CSV</Button>}>Dostępność produktów</PageHeader>

      <SearchWithPagination
        onSearchChange={setFilter("searchTerm")}
        searchValue={filters.searchTerm ?? ""}
        page={filters.page}
        onPageChange={setFilter("page")}
        pageSize={limit}
        itemCount={productAvailabilityQuery.data?.totalCount ?? 0}
      />
      <FilterList>
        <SelectFilter
          label="Regiony"
          initiallySelectedValues={filters.regions ?? []}
          options={regionsOptions}
          onSelectedChange={(options) => setFilter("regions")(options.map((o) => o.value))}
          variant="multi-select"
        />
      </FilterList>
      <QuerySkeleton query={productAvailabilityQuery}>
        {(data) => <ProductAvailabilityTable items={data.items} isLoading={productAvailabilityQuery.isFetching} />}
      </QuerySkeleton>
    </Page>
  );
};

const escapeCsvValue = (value: string | undefined) => {
  const val = value ?? "";
  if (val.includes(",") || val.includes('"') || val.includes("\n")) {
    return `"${val.replace(/"/g, '""')}"`;
  }
  return val;
};

const translateStatus = (status: "available" | "unavailable"): string => {
  return status === "available" ? "Dostępny" : "Niedostępny";
};
